/**
 * Campaign, Flow & Email API — live call sites only.
 *
 * W11b prune: 28 of the 43 methods here had ZERO callers (verified by a
 * full-client call-site census) — including the entire abtestsApi object.
 * Dead client wrappers rot: they encode request shapes nobody exercises
 * and read as capability where none is wired. The SERVER routes they
 * addressed still exist and stay tested server-side; re-add a wrapper
 * here the day a screen actually calls it.
 */
import type { ItemResponse, ListResponse } from '../../types/api.js';
import type { Campaign, Flow, FlowNode, FlowWelcomeRole, EmailTemplate, EmailSendResponse, Customer } from '../../types/models.js';
import { apiRequest } from './core.js';

/** 202 for a queued blast, delivery counts for an inline (test) send. */
export type CampaignSendResponse =
  | { queued: true; jobId: string; campaignId: string }
  | { queued?: false; total: number; sent: number; failed: number };

/** Which sample recipient the preview renders for. */
export type PreviewRecipient = 'named' | 'unnamed';

/** `GET /api/campaigns/:id/preview` — the send's own rendering, for one sample recipient. */
export interface CampaignPreview {
  campaignId: string;
  status: string;
  from: { email: string; name: string };
  replyTo: string | null;
  subject: string;
  html: string;
  hasContent: boolean;
  audience: { segmentId: string | null; segmentName: string | null };
  sampleRecipient: { firstName: string; lastName: string; email: string };
}

export const campaignsApi = {
  /**
   * `POST /api/campaigns` — always existed; this wrapper did not.
   *
   * The W11b prune removed 28 wrappers with zero call sites and took `create`
   * with them, so the Campaigns page could list, send, pause and duplicate
   * campaigns it had no way to make. The only remaining creator was the AI
   * assistant's MCP tools, which is what "created elsewhere" meant.
   *
   * `type` is required by the server's Zod schema (`email | whatsapp | push`);
   * `segmentId` is the TARGETING id — `segment` is only a display name, and a
   * campaign saved without the id broadcasts to every customer.
   */
  create: (data: {
    name: string;
    type: 'email' | 'whatsapp' | 'push';
    subject?: string;
    content?: string;
    segment?: string;
    segmentId?: string;
    scheduledAt?: string;
  }) => apiRequest<ItemResponse<Campaign>>('/campaigns', { method: 'POST', body: JSON.stringify(data) }),
  list: (params?: { search?: string; status?: string; type?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
    const searchParams = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v !== undefined) searchParams.set(k, String(v)); });
    return apiRequest<ListResponse<Campaign>>(`/campaigns?${searchParams}`);
  },
  /**
   * `GET /api/campaigns/:id` — the list omits `content`, so opening a saved
   * draft for editing needs the whole row.
   */
  get: (id: string) => apiRequest<ItemResponse<Campaign>>(`/campaigns/${id}`),
  /** `PUT /api/campaigns/:id` — saving a draft that was opened from its row. */
  update: (id: string, data: {
    name?: string;
    subject?: string;
    content?: string;
    segment?: string;
    segmentId?: string;
  }) => apiRequest<ItemResponse<Campaign>>(`/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateStatus: (id: string, status: string) => apiRequest<ItemResponse<Campaign>>(`/campaigns/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  duplicate: (id: string) => apiRequest<ItemResponse<Campaign>>(`/campaigns/${id}/duplicate`, { method: 'POST' }),
  // A real blast is handed to the campaign-send worker and answered 202
  // `{ queued: true }`; only a test send (a handful of addresses) still
  // returns delivery counts from the request itself.
  send: (id: string) => apiRequest<ItemResponse<CampaignSendResponse>>(`/campaigns/${id}/send`, { method: 'POST' }),
  // Read-only: the email as a sample recipient would receive it. Sends nothing.
  // `unnamed` previews a recipient with no name on file — the send's "there" fallback.
  preview: (id: string, recipient: PreviewRecipient = 'named') =>
    apiRequest<ItemResponse<CampaignPreview>>(
      recipient === 'unnamed' ? `/campaigns/${id}/preview?recipient=unnamed` : `/campaigns/${id}/preview`,
    ),
};

export const flowsApi = {
  // `limit` is optional: callers that need every flow (the Hub totals) ask for
  // the API's 100-per-page cap instead of the 20-row default.
  list: (params?: { limit?: number }) =>
    apiRequest<ListResponse<Flow>>(params?.limit ? `/flows?limit=${params.limit}` : '/flows'),
  get: (id: string) => apiRequest<ItemResponse<Flow>>(`/flows/${id}`),
  // Server schema accepts `trigger` as a string (the eventType). Callers
  // that hold a `FlowTrigger` object must pass `.eventType`.
  // `segmentId` (optional, additive) targets the flow at a saved segment —
  // the server enforces flow.segmentId for segment-scoped automations.
  create: (data: { name: string; description?: string; trigger: string; nodes?: FlowNode[]; segmentId?: string }) =>
    apiRequest<ItemResponse<Flow>>('/flows', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Flow>) => apiRequest<ItemResponse<Flow>>(`/flows/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  // Make this flow THE welcome every new person gets; the server clears the
  // mark on every other flow (V63).
  makeWelcome: (id: string) =>
    apiRequest<ItemResponse<{ id: string; welcome: FlowWelcomeRole | null }>>(`/flows/${id}/welcome`, { method: 'POST' }),
  updateStatus: (id: string, status: string) => apiRequest<ItemResponse<Flow>>(`/flows/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  createFromTrigger: (triggerId: string) => apiRequest<ItemResponse<Flow>>('/flows/create-from-trigger', { method: 'POST', body: JSON.stringify({ triggerId }) }),
  // Backend shape (envelope unwrapped by apiRequest):
  //   data: { success: boolean; logs: string[]; error?: string; customer?: unknown }
  // The outer 200 OK doesn't mean the test ran — `data.success === false` means
  // the flow definition is invalid (e.g. email node with null templateId).
  test: (id: string, customerData?: Partial<Customer>) =>
    apiRequest<ItemResponse<{ success: boolean; logs: string[]; error?: string; customer?: unknown }>>(
      `/flows/${id}/test`,
      { method: 'POST', body: JSON.stringify({ customerData }) },
    ),
  // V63 owner test send. The server picks the recipient (the workspace
  // owner) — the request can only name a step. No `nodeId` = every email step.
  sendTest: (id: string, nodeId?: string) =>
    apiRequest<ItemResponse<OwnerTestSendResult>>(`/flows/${id}/send-test`, {
      method: 'POST',
      body: JSON.stringify(nodeId ? { nodeId } : {}),
    }),
  testRecipient: () => apiRequest<ItemResponse<{ recipient: string | null }>>('/flows/test-recipient'),
};

export interface OwnerTestSendResult {
  recipient: string;
  sent: number;
  failed: number;
  steps: Array<{ nodeId: string; subject: string; success: boolean; messageId?: string; error?: string }>;
}

export interface FlowNodeMetric {
  nodeId: string;
  nodeName?: string;
  sentCount: number;
  deliveredCount: number;
  openCount: number;
  clickCount: number;
  openRate: number;  // 0–1 fraction
  clickRate: number; // 0–1 fraction
}

/**
 * Flow-level totals (aggregated by flowId) beside the per-node breakdown. The
 * per-node buckets key on `metadata.nodeId`, which flow rows carry only since
 * V63 — so totals must come from these fields, not from summing `nodeMetrics`.
 */
export interface FlowMetricsSummary {
  sentCount?: number;
  openCount?: number;
  clickCount?: number;
  nodeMetrics: FlowNodeMetric[];
  /** Flow events with no step id — sent before per-step tracking (V63). */
  untrackedEvents?: number;
}

export const emailsApi = {
  /**
   * Per-node send/open/click metrics for a flow (Klaviyo-style published-flow
   * view). NB: the analytics router is mounted under /emails/analytics — the
   * old `/emails/flow/:id` path matched no route and 404'd for every flow.
   */
  getFlowMetrics: (flowId: string) =>
    apiRequest<ItemResponse<{ flowId: string; metrics: FlowMetricsSummary }>>(`/emails/analytics/flow/${flowId}`),
  // Built-in (code) + this workspace's saved custom templates, built-ins first.
  // Templates live under /emails/templates (sendingRouter owns /emails root).
  library: () => apiRequest<ListResponse<EmailTemplate>>('/emails/templates/library'),
  create: (data: { name: string; subject: string; html: string; category?: string; type?: string }) => apiRequest<ItemResponse<EmailTemplate>>('/emails/templates', { method: 'POST', body: JSON.stringify({ category: 'custom', ...data }) }),
  // Server schema: `to` must be an object { email, name? } not a string.
  // Sending a string returns 400 VALIDATION_ERROR.
  send: (data: { to: { email: string; name?: string }; templateId?: string; subject?: string; html?: string; data?: Record<string, unknown> }) => apiRequest<ItemResponse<EmailSendResponse>>('/emails/send', { method: 'POST', body: JSON.stringify(data) })
};
